import { useEffect, useState } from 'react'
import { useAppSelector, useAppDispatch } from 'store/hooks'
import { useAutoScroll } from 'utils/hooks'
import {
    addRoute,
    updateRouteParams,
    updateRouteTickets,
    cleanTransport,
    setEditRoute,
    RouteType,
    DEFAULT_QUANTITY,
} from 'store/transport-slice'
import { useTransport } from 'store/transport-slice/hooks'
import {
    TransportDepartureType,
    TransportRouteType,
} from 'store/api-slice/types'
import { Wrapper, Creator, OriginalTicket } from './BusTicketCreator.styles'
import AddItem from 'components/shared/AddItem'
import { BusRoute } from 'routes/Upsell/Bus/fields/BusRoute'
import { BusDestination } from 'routes/Upsell/Bus/fields/BusDestination'
import {
    BusDatesAndHours,
    RouteDetails,
} from 'routes/Upsell/Bus/fields/BusDatesAndHours'
import { BusPeople } from 'routes/Upsell/Bus/fields/BusPeople'
import { BusSummary } from 'routes/Upsell/Bus/fields/BusSummary'
import OrderedRoute from 'routes/Upsell/Bus/fields/OrderedRoute'
import { FormattedMessage } from 'react-intl'
import Loader from 'components/shared/Loader'
import { ShuttleInfo } from 'routes/Upsell/Bus/fields/ShuttleInfo/ShuttleInfo'
import { BUS_TICKETS_LIMIT } from 'constants/limits'
import Box from 'components/shared/Box'
import TicketRow from 'components/shared/Reservation/ticketRows/TicketRow'
import Text from 'components/shared/Text'
import TransportItem from 'components/shared/Reservation/ticketRows/TransportItem'
import { TimeBetweenTripsWarning } from 'routes/Upsell/Bus/fields/TimeBetweenTripsWarning'
import moment from 'moment'
import { TransportBlockingError } from 'routes/Upsell/types'
import { BusTicketCreatorProps } from './BusTicketCreator.types'

export const BusTicketCreator = ({
    hasAltEditMode = false,
}: BusTicketCreatorProps) => {
    const dispatch = useAppDispatch()

    const [currentRouteIndex, setCurrentRouteIndex] = useState(0)
    const [blockingError, setBlockingError] =
        useState<TransportBlockingError>(null)
    const [isBookingConflictError, setIsBookingConflictError] = useState(false)
    const [isBusRouteDataFetched, setIsBusRouteDataFetched] = useState(false)
    const [isDeparturePlacesDataFetched, setIsDeparturePlacesDataFetched] =
        useState(false)
    const [seatsLimitTo, setSeatsLimitTo] = useState<number>(BUS_TICKETS_LIMIT)
    const [seatsLimitFrom, setSeatsLimitFrom] =
        useState<number>(BUS_TICKETS_LIMIT)
    const [isActionInProgress, setIsActionInProgress] = useState(false)
    const [isTimeBetweenTripsTooShort, setIsTimeBetweenTripsTooShort] =
        useState(false)
    const { dates: parkDates } = useAppSelector((state) => state.ticket)
    const areInitialDataFetched =
        isBusRouteDataFetched &&
        isDeparturePlacesDataFetched &&
        blockingError !== 'calendar'
    const { routes, edited } = useAppSelector((state) => state.transport)

    const [isChangingDates, setIsChangingDates] = useState(false)

    useEffect(() => {
        if (!routes || !routes[currentRouteIndex]) {
            setIsChangingDates(false)
            return
        }

        const ticketDetail = routes[currentRouteIndex].ticket.detail
        const { date, returnDate, time, returnTime, routeType } = ticketDetail

        if (routeType === 'to_from_park' && time && returnTime) {
            if (isChangingDates) {
                const dateFrom = moment(`${date} ${time}`, 'YYYY_MM_DD hh:mm')
                const dateTo = moment(
                    `${returnDate} ${returnTime}`,
                    'YYYY_MM_DD hh:mm'
                )
                const duration = moment.duration(dateTo.diff(dateFrom))
                const hours = duration.asHours()

                if (hours <= 1) {
                    setIsTimeBetweenTripsTooShort(true)
                } else {
                    setIsTimeBetweenTripsTooShort(false)
                }
            }
        } else {
            setIsTimeBetweenTripsTooShort(false)
        }

        setIsChangingDates(false)
    }, [isChangingDates, routes, currentRouteIndex])

    const { getInitialData, removeRouteFromCart } =
        useTransport(currentRouteIndex)
    const { calculateScroll, animatePendingScroll, scrollIndex } =
        useAutoScroll()

    let scrollTimeout: ReturnType<typeof setTimeout>

    useEffect(
        () => () => {
            dispatch(cleanTransport())
            clearTimeout(scrollTimeout)
        },
        []
    )

    useEffect(() => {
        animatePendingScroll(`transport-ticket-${routes.length - 1}`)
        animatePendingScroll(`transport-ticket-${currentRouteIndex}`)
        animatePendingScroll('transport')
    }, [routes.length, scrollIndex])

    useEffect(() => {
        setIsBookingConflictError(false)
        setSeatsLimitTo(BUS_TICKETS_LIMIT)
        setSeatsLimitFrom(BUS_TICKETS_LIMIT)
    }, [currentRouteIndex])

    const handleSetRouteOption = (
        routeIndex: number,
        value: TransportRouteType
    ) => {
        dispatch(
            updateRouteParams({
                index: routeIndex,
                routeType: value,
                defaultDates: parkDates,
            })
        )
        setSeatsLimitTo(BUS_TICKETS_LIMIT)
        setSeatsLimitFrom(BUS_TICKETS_LIMIT)
        setIsChangingDates(true)
    }
    const handleCityChange = (
        routeIndex: number,
        value: TransportDepartureType
    ) => {
        dispatch(
            updateRouteParams({
                index: routeIndex,
                departurePlace: value,
                defaultDates: parkDates,
            })
        )
        setSeatsLimitTo(BUS_TICKETS_LIMIT)
        setSeatsLimitFrom(BUS_TICKETS_LIMIT)
    }
    const handleSetDateTo = (
        routeIndex: number,
        value: string,
        isRequiresPurchaseTimeSlot: boolean
    ) => {
        dispatch(
            updateRouteTickets({
                index: routeIndex,
                ticketDirection: 'to_park',
                date: value,
                isRequiresPurchaseTimeSlot,
            })
        )
        setSeatsLimitTo(BUS_TICKETS_LIMIT)
        setSeatsLimitFrom(BUS_TICKETS_LIMIT)
    }
    const handleSetDateFrom = (
        routeIndex: number,
        value: string,
        isRequiresPurchaseTimeSlot: boolean
    ) => {
        dispatch(
            updateRouteTickets({
                index: routeIndex,
                ticketDirection: 'from_park',
                date: value,
                isRequiresPurchaseTimeSlot,
            })
        )
        setSeatsLimitTo(BUS_TICKETS_LIMIT)
        setSeatsLimitFrom(BUS_TICKETS_LIMIT)
    }
    const handleSetTimeTo = (
        routeIndex: number,
        value: string,
        isRequiresPurchaseTimeSlot: boolean
    ) => {
        dispatch(
            updateRouteTickets({
                index: routeIndex,
                ticketDirection: 'to_park',
                time: value,
                isRequiresPurchaseTimeSlot,
            })
        )
        setIsChangingDates(true)
    }
    const handleSetTimeFrom = (
        routeIndex: number,
        value: string,
        isRequiresPurchaseTimeSlot: boolean
    ) => {
        dispatch(
            updateRouteTickets({
                index: routeIndex,
                ticketDirection: 'from_park',
                time: value,
                isRequiresPurchaseTimeSlot,
            })
        )
        setIsChangingDates(true)
    }
    const handleSetTicketsQuantity = (
        routeIndex: number,
        value: number,
        isRequiresPurchaseTimeSlot: boolean
    ) => {
        dispatch(
            updateRouteTickets({
                index: routeIndex,
                quantity: value,
                isRequiresPurchaseTimeSlot,
            })
        )
    }

    const handleAddRoute = () => {
        dispatch(addRoute(getInitialData()))
        calculateScroll({
            trigerringSection: 'transport',
            trigerringAction: 'add',
            index: routes.length,
        })
        setCurrentRouteIndex(routes.length)
    }

    const handleEdit = (routeIndex: number) => {
        setCurrentRouteIndex(routeIndex)
        setIsChangingDates(true)
        setSeatsLimitTo(BUS_TICKETS_LIMIT)
        setSeatsLimitFrom(BUS_TICKETS_LIMIT)
        dispatch(
            setEditRoute({
                index: routeIndex,
                editState: 'editing',
            })
        )
        calculateScroll({
            trigerringSection: 'transport',
            trigerringAction: 'edit',
            index: routeIndex,
        })
    }

    const handleSeatsLimit = (limit: number, direction: string) => {
        if (direction === 'to') setSeatsLimitTo(limit)
        if (direction === 'from') setSeatsLimitFrom(limit)
    }

    const handleRouteDetails = ({
        routeIndex,
        duration,
        unitPrice,
        productId,
        isRequiresPurchaseTimeSlot,
    }: RouteDetails) => {
        if (routeIndex === undefined) return

        dispatch(
            updateRouteParams({
                index: routeIndex,
                duration,
                unitPrice,
            })
        )
        dispatch(
            updateRouteTickets({
                index: routeIndex,
                productId,
                isRequiresPurchaseTimeSlot,
            })
        )
    }

    let originPrice = 0
    let originQuantity = 0

    if (edited) {
        originPrice = edited.origin.unitPrice || 0
        originQuantity = edited.origin.ticket.quantity || 0
    }

    const handleRemove = () => {
        setIsActionInProgress(true)
        removeRouteFromCart().then(() => {
            setIsActionInProgress(false)
        })
    }

    return (
        <Wrapper gap={16} isFullWidth id="transport">
            {hasAltEditMode && (
                <Text variant="bodyLarge" color="grayscale_900">
                    {routes.length ? (
                        <FormattedMessage
                            id="selfcare.cabana.caption_added_to_order"
                            defaultMessage="Dodano do zamówienia"
                        />
                    ) : (
                        <FormattedMessage
                            id="selfcare.transport.caption_add_to_order"
                            defaultMessage="Wybierz dodatkowy bilet"
                        />
                    )}
                </Text>
            )}
            {/* TODO: move into standalone component/s */}
            {routes.map((route, index) => {
                const {
                    ticket: {
                        cartItemId,
                        quantity,
                        detail: {
                            routeType,
                            departurePlace,
                            time: timeTo,
                            date: dateTo,
                            returnTime: timeFrom,
                            returnDate: dateFrom,
                            isRequiresPurchaseTimeSlot,
                        },
                    },
                    duration,
                    unitPrice,
                    isSubmitted,
                } = route

                const isEdited = !!edited && edited.index === index
                const ticketsQuantity = quantity || DEFAULT_QUANTITY
                const hasLimitError =
                    quantity &&
                    (seatsLimitFrom < quantity || seatsLimitTo < quantity)

                const isModified = (() => {
                    const { detail: originDetail, quantity: originQuantity } =
                        edited?.origin.ticket || {}
                    const {
                        date: originDate,
                        returnDate: originReturnDate,
                        time: originTime,
                        returnTime: originReturnTime,
                        departurePlace: originDeparturePlace,
                        routeType: originRouteType,
                    } = originDetail || {}
                    const { detail: currentDetail, quantity: currentQuantity } =
                        route.ticket
                    const {
                        date: currentDate,
                        returnDate: currentReturnDate,
                        time: currentTime,
                        returnTime: currentReturnTime,
                        departurePlace: currentDeparturePlace,
                        routeType: currentRouteType,
                    } = currentDetail || {}

                    return (
                        originDate !== currentDate ||
                        originReturnDate !== currentReturnDate ||
                        originTime !== currentTime ||
                        originReturnTime !== currentReturnTime ||
                        originDeparturePlace !== currentDeparturePlace ||
                        originRouteType !== currentRouteType ||
                        originQuantity !== currentQuantity
                    )
                })()

                if (isSubmitted && !isEdited)
                    return (
                        <OrderedRoute
                            id={`transport-ticket-${index}`}
                            key={`ordered${cartItemId}`}
                            routeIndex={index}
                            ticketsQuantity={ticketsQuantity}
                            unitPrice={unitPrice || 0}
                            editHandler={() => handleEdit(index)}
                        />
                    )

                return (
                    <Creator
                        isFullWidth
                        pl={[16, 40]}
                        pr={[16, 40]}
                        pt={[24, 40]}
                        pb={[24, 40]}
                        gap={[32, 40]}
                        key={cartItemId}
                        id={`transport-ticket-${index}`}
                    >
                        {hasAltEditMode && isEdited && (
                            <OriginalTicket
                                isFullWidth
                                gap={[16, 40]}
                                pb={[16, 32]}
                                mb={[6, 0]}
                                direction={['column', 'row']}
                                align={['flex-start', 'center']}
                            >
                                <TicketRow price={originPrice * originQuantity}>
                                    <TransportItem
                                        transportTicket={{
                                            original: {
                                                detail: {
                                                    ...edited.origin.ticket
                                                        .detail,
                                                    orderTransportTicketId:
                                                        route.ticket
                                                            .cartItemId || '',
                                                },
                                                price: route.ticket.totalPrice,
                                                quantity:
                                                    edited.origin.ticket
                                                        .quantity,
                                            },
                                            changes: {
                                                detail: {
                                                    ...edited.origin.ticket
                                                        .detail,
                                                    orderTransportTicketId:
                                                        route.ticket
                                                            .cartItemId || '',
                                                },
                                                price: route.ticket.totalPrice,
                                                quantity:
                                                    edited.origin.ticket
                                                        .quantity,
                                            },
                                        }}
                                        hasCounter
                                    />
                                </TicketRow>
                                <Box
                                    direction={['row-reverse', 'row']}
                                    gap={16}
                                >
                                    {isActionInProgress ? (
                                        <Loader hasNoShrink />
                                    ) : (
                                        <Text
                                            as={'button'}
                                            variant="bodyStrong"
                                            color={'grayscale_700'}
                                            onClick={handleRemove}
                                        >
                                            <FormattedMessage
                                                id="global.btn.remove"
                                                defaultMessage="Usuń"
                                            />
                                        </Text>
                                    )}
                                </Box>
                            </OriginalTicket>
                        )}

                        <BusRoute
                            routeId={index}
                            routeOption={routeType}
                            setRouteOption={(value) =>
                                handleSetRouteOption(index, value)
                            }
                            setIsDataFetched={setIsBusRouteDataFetched}
                        />
                        {isBusRouteDataFetched && (
                            <BusDestination
                                routeId={index}
                                startCity={departurePlace}
                                setStartCity={(value) =>
                                    handleCityChange(index, value)
                                }
                                routeOption={routeType}
                                duration={duration}
                                unitPrice={unitPrice}
                                setIsDataFetched={
                                    setIsDeparturePlacesDataFetched
                                }
                            />
                        )}
                        {blockingError === 'calendar' && <Loader />}
                        {areInitialDataFetched && (
                            <>
                                {routeType && departurePlace && (
                                    <BusDatesAndHours
                                        routeIndex={index}
                                        routeId={index}
                                        routeType={routeType}
                                        departurePlace={departurePlace}
                                        ticketsQuantity={ticketsQuantity}
                                        dateTo={dateTo}
                                        dateFrom={
                                            isEdited
                                                ? dateFrom || dateTo
                                                : dateFrom
                                        }
                                        setDateTo={handleSetDateTo}
                                        setDateFrom={handleSetDateFrom}
                                        timeTo={timeTo}
                                        timeFrom={timeFrom}
                                        setTimeTo={handleSetTimeTo}
                                        setTimeFrom={handleSetTimeFrom}
                                        setSeatsLimit={handleSeatsLimit}
                                        setError={(value) =>
                                            setBlockingError(value)
                                        }
                                        isBookingConflictError={
                                            isBookingConflictError
                                        }
                                        setRouteDetails={handleRouteDetails}
                                        edited={edited}
                                    />
                                )}

                                {isRequiresPurchaseTimeSlot === false && (
                                    <ShuttleInfo
                                        departurePlace={departurePlace}
                                    />
                                )}

                                {isTimeBetweenTripsTooShort && (
                                    <TimeBetweenTripsWarning />
                                )}

                                {!!quantity && (
                                    <BusPeople
                                        ticketsAmount={quantity}
                                        limit={BUS_TICKETS_LIMIT}
                                        setTicketsAmount={(amount) =>
                                            handleSetTicketsQuantity(
                                                index,
                                                amount,
                                                isRequiresPurchaseTimeSlot ||
                                                    true
                                            )
                                        }
                                        index={index}
                                    />
                                )}

                                <BusSummary
                                    routeIndex={index}
                                    isEdited={isEdited}
                                    isCtaDisabled={
                                        hasLimitError ||
                                        !!blockingError ||
                                        (!isModified && isEdited)
                                    }
                                    setBookingConflict={(isError) =>
                                        setIsBookingConflictError(isError)
                                    }
                                    hasAltEditMode={hasAltEditMode && isEdited}
                                    originTotalPrice={
                                        originQuantity * originPrice
                                    }
                                />
                            </>
                        )}
                    </Creator>
                )
            })}
            {routes.every((route: RouteType) => route.isSubmitted) && !edited && (
                <AddItem
                    onClick={handleAddRoute}
                    testId="transport-button-add-another"
                >
                    <FormattedMessage
                        id="global.btn.add_another_ticket"
                        defaultMessage="Dodaj kolejny bilet"
                    />
                </AddItem>
            )}
        </Wrapper>
    )
}

BusTicketCreator.displayName = 'BusTicketCreator'
